import React from 'react';
import Modal from '../Modal';

/**
 * Modal for adding and editing leads
 * Financial fields (estimated amount, discount) hidden from team members
 */
const LeadFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  serviceTypes,
  isOwner,
  isEditing = false,
  editingLead = null,
  hasCompleteAddress,
  isSubmitting = false,
}) => {
  // US States for dropdown
  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Lead: ${editingLead?.name || ''}` : 'Add New Lead'}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Incomplete Warning for Edit */}
        {isEditing && editingLead && !hasCompleteAddress(editingLead) && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <p className="text-orange-800 text-sm font-medium">⚠️ Complete Required Fields</p>
            <p className="text-orange-600 text-xs mt-1">
              Fill in all address fields to convert this lead to a customer.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1">
            <label className="label">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="John Smith"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="label">Phone *</label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
              placeholder="(980) 231-1515"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1">
            <label className="label">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              placeholder="john@example.com"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="label">Service Interest</label>
            <select
              value={formData.serviceInterest}
              onChange={(e) => setFormData({ ...formData, serviceInterest: e.target.value })}
              className="input"
            >
              <option value="">Select service...</option>
              {serviceTypes.map((service) => (
                <option key={service.id} value={service.name}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Address Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-semibold text-blue-800">
            📍 Property Address {isEditing ? '(Required for conversion)' : '(All fields required)'}
          </p>
          
          <div>
            <label className="label">Street Address *</label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input"
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-6 sm:col-span-3">
              <label className="label">City *</label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input"
                placeholder="Mooresville"
              />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <label className="label">State *</label>
              <select
                required
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="input"
              >
                <option value="">--</option>
                {usStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-3 sm:col-span-2">
              <label className="label">Zip Code *</label>
              <input
                type="text"
                required
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                className="input"
                placeholder="28117"
                maxLength={10}
              />
            </div>
          </div>
        </div>

        <div>
          <label className="label">Message</label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="input"
            rows="2"
            placeholder="Customer's message or additional details..."
          />
        </div>

        {/* OWNER ONLY: Financial fields */}
        {isOwner && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Estimated Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.estimatedAmount}
                  onChange={(e) => setFormData({ ...formData, estimatedAmount: e.target.value })}
                  className="input pl-7"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="flex flex-col justify-end gap-2 pb-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={formData.isVerified}
                  onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Verified</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={formData.isDiscountEligible}
                  onChange={(e) => setFormData({ ...formData, isDiscountEligible: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Discount Eligible</span>
              </label>
            </div>
          </div>
        )}

        {/* Team member: Just verified checkbox (no financial info) */}
        {!isOwner && (
          <div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={formData.isVerified}
                onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Verified Contact</span>
            </label>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary flex-1 justify-center"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary flex-1 justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Lead'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default LeadFormModal;